import type { MaybeRef } from '@vueuse/core'
import { useToast } from 'vue-toastification'
import { parseImageKitOptions } from '@/plugins/image-kit'

export { useToast }

interface IFloatingElementOptions {
  defaultState?: boolean
  enableMouseLeave?: boolean
  enableToggleOnClick?: boolean
}

const checkParent = (elementToCheck: HTMLElement, parentElement: HTMLElement, depth = 10): boolean => {
  if (elementToCheck === parentElement)
    return true

  if (depth === 0)
    return false

  if (elementToCheck?.parentElement)
    return checkParent(elementToCheck.parentElement, parentElement, depth - 1)

  return false
}

export function useFloatingElement(
  target: Ref<HTMLElement | undefined | null>,
  options: IFloatingElementOptions = {
    defaultState: false,
    enableMouseLeave: true,
    enableToggleOnClick: true,
  },
  triggerRef?: Ref<any>,
): [Ref<boolean>, (show?: boolean) => void] {
  const [show, toggleShow] = useToggle(options?.defaultState ?? false)
  const shouldHideOnMouseLeave = ref(false)

  function mouseEnter() {
    shouldHideOnMouseLeave.value = true
  }

  function mouseLeave() {
    if (shouldHideOnMouseLeave.value) {
      toggleShow(false)
      shouldHideOnMouseLeave.value = false
    }
  }

  useEventListener(target, 'mouseenter', mouseEnter)

  if (options.enableMouseLeave)
    useEventListener(target, 'mouseleave', mouseLeave)

  onClickOutside(target, (event) => {
    // Check if the target is a child of the trigger
    if (!triggerRef?.value?.$el || !checkParent(event.target as HTMLElement, triggerRef.value.$el as HTMLElement))
      toggleShow(false)
  })

  if (options.enableToggleOnClick)
    useEventListener(target, 'click', () => toggleShow(false))

  return [
    show,
    toggleShow,
  ]
}

export interface IOptimizeImageOptions {
  width?: number
  height?: number
  aspectRatio?: [number, number]
  quality?: number
  blur?: number
  backgroundColor?: string
  crop?: 'at_max' | 'at_least' | 'force' | 'auto'
  focus?: 'auto' | 'center' | 'top' | 'left' | 'face'
  format?: 'webp' | 'jpg' | 'png'
}
export type IOptimizeImageOptionsRef = {
  [K in keyof IOptimizeImageOptions]: MaybeRef<IOptimizeImageOptions[K]> | ComputedRef<IOptimizeImageOptions[K]>
}

const imagekitUrl = 'https://ik.imagekit.io/b6q4ycaer'

const URL_DOMAINS_TO_REPLACE = [
  's3.us-east-2.amazonaws.com/files.snackprompt.com',
  'files.snackprompt.com.s3-website.us-east-2.amazonaws.com',
  'files.snackprompt.com',
  'snackprompt.com',
]

const isProcessableUrl = (url: string) => {
  if (!url || url.includes('ik.imagekit.io/b6q4ycaer'))
    return false
  return URL_DOMAINS_TO_REPLACE.some(domain => url.includes(domain))
}

function replaceImageUrl(originalUrl: string, imagekitUrl: string): string {
  const URL_REGEX = new RegExp(
    `https?://(${URL_DOMAINS_TO_REPLACE.join('|').replace(/\./g, '\\.')})`,
    'gi',
  )

  return originalUrl.replace(URL_REGEX, imagekitUrl)
}

export function optimizeImage(url?: MaybeRef<string | undefined>, options?: IOptimizeImageOptionsRef) {
  const originalUrl = unref(url)

  if (!originalUrl || !isProcessableUrl(originalUrl))
    return originalUrl

  let optimizedUrl = replaceImageUrl(originalUrl, imagekitUrl)

  if (options) {
    const unrefOptions = Object.fromEntries(
      Object.entries(options).map(([key, value]) => [key, unref(value)]),
    )

    const parsedOptions = parseImageKitOptions(unrefOptions)
    if (parsedOptions)
      optimizedUrl += parsedOptions
  }

  return optimizedUrl
}

export function getBlurImage(url?: MaybeRef<string | undefined>, options: Pick<IOptimizeImageOptionsRef, 'width' | 'height' | 'quality' | 'format'> = { quality: 80, format: 'webp' }) {
  const originalUrl = unref(url)

  if (!originalUrl || !isProcessableUrl(originalUrl))
    return originalUrl

  let optimizedUrl = replaceImageUrl(originalUrl, imagekitUrl)
  const unrefOptions = Object.fromEntries(
    Object.entries(options).map(([key, value]) => [key, unref(value)]),
  )

  const parsedOptions = parseImageKitOptions({ ...unrefOptions, blur: 30 })
  if (parsedOptions)
    optimizedUrl += parsedOptions

  return optimizedUrl
}
